import { ref } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useToast } from 'vue-toastification'
import { notificationService } from '@/services/notificationService'

let foregroundListenerAttached = false

export function useNotifications() {
  const notificationPermission = ref(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  )
  const fcmToken = ref(null)
  const authStore = useAuthStore()
  const toast = useToast()

  const requestPermission = async () => {
    try {
      if (typeof Notification === 'undefined') {
        console.warn('Notification API not supported')
        toast.warning('Notifications are not supported in this browser')
        return false
      }

      const userId = authStore.user?.uid || authStore.userProfile?.uid || null
      const token = await notificationService.requestPermission(userId)

      notificationPermission.value = Notification.permission

      if (token) {
        fcmToken.value = token
        return true
      }

      return Notification.permission === 'granted'
    } catch (error) {
      console.error('An error occurred while enabling notifications:', error)
      return false
    }
  }

  const showForegroundBanner = (payload) => {
    const title = payload.notification?.title || payload.data?.title || 'New Notification'
    const body = payload.notification?.body || payload.data?.body || ''

    // App visible: in-app toast. Hidden/minimized PWA tab: OS banner.
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        try {
          const n = new Notification(title, {
            body,
            icon: '/pwa-192x192.png',
            tag: payload.data?.tag || `fg_${Date.now()}`,
            data: payload.data || {},
          })
          n.onclick = () => {
            window.focus()
            n.close()
          }
          return
        } catch (err) {
          console.warn('Foreground Notification failed, falling back to toast:', err)
        }
      }
    }

    toast.info(body ? `${title}: ${body}` : title, { timeout: 5000 })
  }

  const listenForMessages = () => {
    if (foregroundListenerAttached) return
    foregroundListenerAttached = true
    notificationService.listenForMessages(showForegroundBanner)
  }

  return {
    notificationPermission,
    requestPermission,
    listenForMessages,
    fcmToken,
  }
}
